import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Award,
  ChevronLeft,
  ChevronRight,
  Quote,
  ShieldCheck,
  Star,
  Building2,
  ExternalLink,
} from "lucide-react";
import credibilityData from "../data/credibility.json";
import { useCmsDoc } from "../hooks/useCmsDoc";
import ConsentAwareEmbed from "../components/ConsentAwareEmbed";

/** Contenedor rectangular: logo ocupa 80% del área (proporción 80/100). */
const LogoTile = ({ src, alt, className = "", active = false }) => (
  <div
    className={`flex items-center justify-center bg-white border transition-all duration-300 ${
      active
        ? "border-brand-primary shadow-md ring-2 ring-brand-primary/20"
        : "border-brand-soft hover:border-brand-primary/30 hover:shadow-sm"
    } ${className}`}
  >
    <img
      src={src}
      alt={alt}
      className="w-[80%] h-[80%] object-contain"
      loading="lazy"
    />
  </div>
);

const CredibilityPage = () => {
  const { data: page } = useCmsDoc("credibility", "page", credibilityData.page);
  const { data: providers } = useCmsDoc("credibility", "providers", credibilityData.providers);
  const { data: testimonials } = useCmsDoc(
    "credibility",
    "testimonials",
    credibilityData.testimonials
  );
  const { data: accreditations } = useCmsDoc(
    "credibility",
    "accreditations",
    credibilityData.accreditations
  );
  const [activeIndex, setActiveIndex] = useState(0);

  if (!page || !providers || !testimonials || !accreditations) return null;

  const total = testimonials.items?.length || 0;

  const goTo = (direction) => {
    if (!total) return;
    setActiveIndex((prev) => (prev + direction + total) % total);
  };

  const activeTestimonial = testimonials.items?.[activeIndex];
  if (!activeTestimonial) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="bg-[#fafcfb] min-h-screen font-body selection:bg-brand-accent selection:text-brand-dark"
    >
      <header className="relative pt-40 pb-20 bg-brand-dark overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage:
              "linear-gradient(#39ff14 1px, transparent 1px), linear-gradient(90deg, #39ff14 1px, transparent 1px)",
            backgroundSize: "40px 40px",
          }}
        />
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-brand-accent/10 blur-[120px] rounded-full" />

        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-5xl">
            <motion.div
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              className="flex items-center gap-3 mb-8"
            >
              <span className="w-8 h-[2px] bg-brand-accent" />
              <span className="text-brand-accent font-body font-bold uppercase tracking-[0.4em] text-[11px] md:text-sm">
                {page.eyebrow}
              </span>
            </motion.div>

            <motion.h1
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="font-display text-[clamp(2.8rem,10vw,3.5rem)] text-white font-black leading-[1.1] tracking-tighter flex flex-wrap items-center gap-x-4"
            >
              <span>{page.title}</span>
              <span className="text-brand-accent italic font-light">{page.titleAccent}</span>
            </motion.h1>

            <motion.p
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="text-white/80 text-lg md:text-xl font-medium mt-6 max-w-3xl border-l-2 border-brand-accent/30 pl-6"
            >
              {page.description}
            </motion.p>
          </div>
        </div>
      </header>

      <section className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="flex items-center gap-4 mb-4">
            <ShieldCheck className="text-brand-primary" size={32} />
            <h2 className="font-display text-3xl md:text-4xl font-bold text-brand-dark tracking-tight">
              {providers.title}
            </h2>
          </div>
          <p className="text-brand-dark/70 text-lg max-w-3xl mb-12 leading-relaxed">
            {providers.subtitle}
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {providers.items.map((provider, index) => (
              <motion.div
                key={provider.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="group p-5 bg-white rounded-[30px] border border-brand-soft hover:border-brand-primary/25 hover:shadow-xl transition-all duration-300"
              >
                <div className="absolute pointer-events-none" />
                <LogoTile
                  src={provider.logo}
                  alt={provider.name}
                  className="w-full aspect-[5/4] rounded-2xl bg-[#f8faf9]"
                />
                <div className="mt-4 space-y-3">
                  <p className="text-sm font-bold text-brand-dark leading-tight min-h-[40px]">
                    {provider.name}
                  </p>
                  {provider.website ? (
                    <a
                      href={provider.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-primary/10 text-brand-primary font-bold text-[11px] uppercase tracking-widest hover:bg-brand-primary hover:text-white transition-colors"
                      aria-label={`Ir al sitio web de ${provider.name}`}
                    >
                      Visitar sitio
                      <ExternalLink size={14} />
                    </a>
                  ) : (
                    <span className="inline-flex items-center px-4 py-2 rounded-full bg-brand-soft text-brand-dark/50 font-bold text-[11px] uppercase tracking-widest">
                      Sitio no disponible
                    </span>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-brand-soft/40">
        <div className="container mx-auto px-6">
          <div className="flex items-center gap-4 mb-4">
            <Quote className="text-brand-primary" size={32} />
            <h2 className="font-display text-3xl md:text-4xl font-bold text-brand-dark tracking-tight">
              {testimonials.title}
            </h2>
          </div>
          <p className="text-brand-dark/70 text-lg max-w-3xl mb-12 leading-relaxed">
            {testimonials.subtitle}
          </p>

          <div className="max-w-5xl mx-auto space-y-6">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3">
              {testimonials.items.map((item, index) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setActiveIndex(index)}
                  className="text-left"
                  aria-label={`Ver testimonio de ${item.company}`}
                  aria-current={index === activeIndex ? "true" : undefined}
                >
                  <LogoTile
                    src={item.logo}
                    alt={item.company}
                    active={index === activeIndex}
                    className="w-full aspect-[5/4] rounded-xl"
                  />
                  <p className="mt-2 text-[11px] font-bold text-brand-dark/70 text-center line-clamp-2 leading-tight">
                    {item.company}
                  </p>
                </button>
              ))}
            </div>

            <div className="bg-white rounded-[40px] shadow-xl border border-brand-soft overflow-hidden">
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeTestimonial.id}
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -30 }}
                  transition={{ duration: 0.35 }}
                  className="grid md:grid-cols-12 gap-0"
                >
                  <div className="md:col-span-4 bg-brand-dark p-8 flex flex-col items-center justify-center text-center">
                    <LogoTile
                      src={activeTestimonial.logo}
                      alt={activeTestimonial.company}
                      active
                      className="w-full max-w-[220px] aspect-[5/4] rounded-2xl mb-6 shadow-2xl"
                    />
                    <div className="flex gap-1 mb-4">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} size={16} className="text-brand-accent fill-brand-accent" />
                      ))}
                    </div>
                    <p className="text-white font-bold text-lg">{activeTestimonial.author}</p>
                    <p className="text-brand-accent/80 text-sm font-medium mt-1 flex items-center gap-2">
                      <Building2 size={14} />
                      {activeTestimonial.company}
                    </p>
                  </div>

                  <div className="md:col-span-8 p-8 md:p-12 flex flex-col justify-center">
                    <Quote className="text-brand-primary/20 mb-4" size={48} />
                    <p className="text-brand-dark/80 text-lg md:text-xl leading-relaxed italic">
                      &ldquo;{activeTestimonial.quote}&rdquo;
                    </p>
                  </div>
                </motion.div>
              </AnimatePresence>

              <div className="flex items-center justify-between px-8 py-6 border-t border-brand-soft bg-[#f8faf9]">
                <button
                  type="button"
                  onClick={() => goTo(-1)}
                  className="flex items-center gap-2 px-4 py-2 rounded-full text-brand-primary font-bold text-sm hover:bg-brand-soft transition-colors"
                  aria-label="Testimonio anterior"
                >
                  <ChevronLeft size={20} />
                  Anterior
                </button>

                <p className="text-sm font-bold text-brand-dark/50 hidden sm:block">
                  {activeIndex + 1} / {total}
                </p>

                <button
                  type="button"
                  onClick={() => goTo(1)}
                  className="flex items-center gap-2 px-4 py-2 rounded-full text-brand-primary font-bold text-sm hover:bg-brand-soft transition-colors"
                  aria-label="Siguiente testimonio"
                >
                  Siguiente
                  <ChevronRight size={20} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="flex items-center gap-4 mb-4">
            <Award className="text-brand-primary" size={32} />
            <h2 className="font-display text-3xl md:text-4xl font-bold text-brand-dark tracking-tight">
              {accreditations.title}
            </h2>
          </div>
          <p className="text-brand-dark/70 text-lg max-w-3xl mb-12 leading-relaxed">
            {accreditations.subtitle}
          </p>

          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {accreditations.highlights.map((item, index) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="p-8 bg-[#f8faf9] rounded-[32px] border border-brand-soft hover:shadow-lg transition-shadow"
              >
                {item.logo ? (
                  <div className="h-16 mb-6 flex items-center">
                    <img
                      src={item.logo}
                      alt={item.title}
                      className="max-h-16 max-w-[180px] object-contain"
                      loading="lazy"
                    />
                  </div>
                ) : (
                  <div className="w-12 h-12 rounded-2xl bg-brand-primary/10 flex items-center justify-center mb-6">
                    <Award className="text-brand-primary" size={22} />
                  </div>
                )}
                <h3 className="font-display text-xl font-bold text-brand-dark mb-3">{item.title}</h3>
                <p className="text-brand-dark/70 leading-relaxed">{item.description}</p>
              </motion.div>
            ))}
          </div>

          <div className="bg-white rounded-[40px] overflow-hidden shadow-2xl border border-brand-soft p-2">
            <div className="px-6 py-4 border-b border-brand-soft">
              <h3 className="font-display text-xl font-bold text-brand-dark">
                {accreditations.catalog.title}
              </h3>
            </div>
            <div style={{ position: "relative", paddingTop: "max(60%, 324px)", width: "100%", height: 0 }}>
              <ConsentAwareEmbed
                title="Catálogo Heyzine"
                description="El catálogo se carga desde Heyzine (tercero). Actívelo si acepta cookies de contenido externo."
                className="absolute inset-0 rounded-none h-full border-0"
                minHeight="100%"
              >
                <iframe
                  style={{ position: "absolute", border: "none", width: "100%", height: "100%", left: 0, top: 0 }}
                  src={accreditations.catalog.embedUrl}
                  title="AGDYSA - Certificaciones y Acreditaciones"
                  loading="lazy"
                  allowFullScreen
                />
              </ConsentAwareEmbed>
            </div>
          </div>
        </div>
      </section>
    </motion.div>
  );
};

export default CredibilityPage;
